import { Link } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";

export default function Landing() {
  const { user } = useAuth();
  const primaryHref = user ? "/app" : "/login";
  const primaryLabel = user ? "App öffnen" : "Jetzt einloggen";

  return (
    <div className="space-y-24 py-8">
      {/* HERO */}
      <section className="text-center max-w-3xl mx-auto">
        <div className="inline-block px-3 py-1 rounded-full bg-court-900/60 border border-court-700 text-xs text-court-200 mb-6 font-mono">
          AI Mock Jury · Zero Data Retention · Powered by OpenRouter
        </div>
        <h1 className="text-5xl md:text-6xl font-bold tracking-tight bg-gradient-to-br from-white to-court-300 bg-clip-text text-transparent">
          Das Schatten-Gericht.
        </h1>
        <p className="mt-6 text-lg text-slate-300 leading-relaxed">
          Lass Deinen Rechtsfall von einem Schwarm unabhängiger KI-Modelle als
          Mock Jury simulieren. Gewinn-Wahrscheinlichkeit, Schadensersatz und
          Vergleichs-Empfehlung — in 30 Sekunden, ohne dass Deine Dokumente
          jemals zum Training verwendet werden.
        </p>
        <div className="mt-10 flex items-center justify-center gap-3">
          <Link
            to={primaryHref}
            className="rounded-lg bg-gradient-to-r from-court-500 to-court-700 hover:from-court-400 hover:to-court-600 px-6 py-3 font-semibold shadow-lg shadow-court-700/30 transition"
          >
            {primaryLabel}
          </Link>
          <Link
            to={user ? "/app/affiliate" : "/login?next=%2Fapp%2Faffiliate"}
            className="rounded-lg border border-slate-700 hover:border-slate-500 px-6 py-3 font-semibold transition"
          >
            25 % Affiliate verdienen
          </Link>
        </div>
      </section>

      {/* FEATURES */}
      <section className="grid md:grid-cols-3 gap-6">
        <Feature
          icon="⚖"
          title="Heterogener LLM-Schwarm"
          body="Claude, DeepSeek und Llama debattieren unabhängig. Keine Einzelmodell-Bias."
        />
        <Feature
          icon="📚"
          title="RAG über Präzedenzfälle"
          body="Vectorize durchsucht BGH- & EuGH-Entscheidungen und liefert die 5 relevantesten in den Prompt."
        />
        <Feature
          icon="🔒"
          title="Zero Data Retention"
          body="store: false bei jedem Call. OpenRouter-Provider mit ZDR-Policy. Keine Trainings-Verwendung."
        />
      </section>

      {/* HOW IT WORKS */}
      <section>
        <h2 className="text-3xl font-bold mb-8 text-center">Wie es funktioniert</h2>
        <div className="grid md:grid-cols-4 gap-4">
          {[
            ["1", "Upload", "PDF Deines Falls hochladen (Mietvertrag, Rechnung, Bescheid)."],
            ["2", "Indexierung", "Cloudflare Vectorize erzeugt semantische Embeddings."],
            ["3", "Schwarm-Debatte", "3+ Modelle stimmen unabhängig über den Ausgang ab."],
            ["4", "Urteil", "Gewichtete Win-Rate, Schadensersatz, Settlement-Empfehlung."],
          ].map(([n, title, body]) => (
            <div
              key={n}
              className="rounded-xl border border-slate-800 bg-slate-900/40 p-6"
            >
              <div className="text-4xl font-mono text-court-400 mb-3">{n}</div>
              <p className="font-semibold">{title}</p>
              <p className="text-sm text-slate-400 mt-1 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING HINT */}
      <section className="max-w-2xl mx-auto text-center">
        <h2 className="text-3xl font-bold mb-4">Pricing</h2>
        <p className="text-slate-400">
          Erste Simulation gratis. Danach 29 EUR pro Fall oder 79 EUR/Monat
          unbegrenzt. Affiliates bekommen 25 % (L1) + 10 % (L2) + 5 % (L3) auf
          jeden realen Verkauf in ihrer Referrer-Chain.
        </p>
      </section>
    </div>
  );
}

function Feature({ icon, title, body }: { icon: string; title: string; body: string }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-6 hover:border-court-600 transition">
      <div className="text-3xl mb-3">{icon}</div>
      <h3 className="font-semibold text-lg">{title}</h3>
      <p className="text-sm text-slate-400 mt-2 leading-relaxed">{body}</p>
    </div>
  );
}
